import { ensureFreshAccessToken, getAccessToken, resolveApiBase } from './client';
// A failed refresh either ended the session (ensureFreshAccessToken already cleared
// auth and redirected: no token left) or was transient (token kept; the user can retry).
async function ensureToken() {
    try {
        await ensureFreshAccessToken();
    }
    catch (error) {
        if (getAccessToken())
            throw error;
        return null;
    }
    return getAccessToken();
}
/**
 * The availability gate answers 503 with the same JSON body as /ai/complete
 * ({ error: <code>, message: <prose> }, see lib/ai/claude.ts) before any
 * stream opens. The prose is what the bubble shows; the code rides along so
 * a caller can branch on it. Anything else (a 400 "Message is required", a
 * proxy's HTML page) is shown as it came.
 */
function parseErrorBody(raw) {
    try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
            const { error, message } = parsed;
            return {
                message: typeof message === 'string' && message ? message : undefined,
                code: typeof error === 'string' && error ? error : undefined,
            };
        }
    }
    catch {
        // not JSON
    }
    return {};
}
export async function sendChatMessage(request, onEvent, signal) {
    let token;
    try {
        token = await ensureToken();
    }
    catch {
        onEvent({ type: 'error', error: 'Could not reach the server. Please try again.' });
        return;
    }
    if (!token) {
        onEvent({ type: 'error', error: 'Authentication required' });
        return;
    }
    const res = await fetch(`${resolveApiBase('/ai/chat')}/ai/chat`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify(request),
        signal,
    });
    if (!res.ok) {
        const raw = await res.text().catch(() => '');
        const { message, code } = parseErrorBody(raw);
        onEvent({ type: 'error', error: message ?? (raw || 'Chat request failed'), code });
        return;
    }
    const reader = res.body?.getReader();
    if (!reader) {
        onEvent({ type: 'error', error: 'No response stream' });
        return;
    }
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
        const { done, value } = await reader.read();
        if (done)
            break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed || !trimmed.startsWith('data:'))
                continue;
            const json = trimmed.slice(5).trim();
            if (!json || json === '[DONE]')
                continue;
            try {
                onEvent(JSON.parse(json));
            }
            catch {
                // skip malformed events
            }
        }
    }
}
